import { useCallback, useEffect, useState } from 'react';
import { capsuleConfigAPI } from '@/lib/api';

const UNIDAD_POR_DEFECTO = { singular: 'Ticket', plural: 'Tickets' };

const EMPTY = {
  intents: [],
  categories: [],
  labels: [],
  priorities: [],
  ticket_statuses: [],
  task_statuses: [],
  requesting_departments: [],
  unidad_de_trabajo: UNIDAD_POR_DEFECTO,
};

/**
 * Carga catálogos de clasificación de una cápsula (intents, categorías, labels,
 * prioridades fijas, estados de ticket, gerencias).
 */
export default function useCapsuleRoutingCatalogs(tenantId, capsuleId) {
  const [catalogs, setCatalogs] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!tenantId || !capsuleId) {
      setCatalogs(EMPTY);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await capsuleConfigAPI.get(tenantId, capsuleId);
      const cfg = res.data || {};
      setCatalogs({
        intents: cfg.intents || [],
        categories: cfg.categories || [],
        labels: cfg.labels || [],
        priorities: cfg.priorities || [],
        ticket_statuses: cfg.ticket_statuses || [],
        task_statuses: cfg.task_statuses || [],
        requesting_departments: cfg.requesting_departments || [],
        unidad_de_trabajo: cfg.unidad_de_trabajo || UNIDAD_POR_DEFECTO,
      });
    } catch (e) {
      setCatalogs(EMPTY);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { ...catalogs, catalogs, loading, error, reload };
}

/** Resuelve name (o id) desde un catálogo [{id,name,...}]. */
export function catalogLabel(items, value, fallback = '') {
  if (!value) return fallback;
  const found = (items || []).find((i) => i.id === value || i.name === value);
  return found?.name || value || fallback;
}

export function catalogColor(items, value, fallback = '#71717a') {
  if (!value) return fallback;
  const found = (items || []).find((i) => i.id === value || i.name === value);
  return found?.color || fallback;
}

/**
 * Cómo llama esta cápsula a lo que trabaja. Mesa de ayuda dice ticket, mantenimiento diría
 * orden, legales expediente; ninguna tiene razón sobre la otra.
 */
export function unidadDeTrabajo(unidad, { plural = false } = {}) {
  const u = unidad || UNIDAD_POR_DEFECTO;
  return (plural ? u.plural : u.singular) || (plural ? 'Tickets' : 'Ticket');
}

/** Si un estado cuenta como en curso, según las banderas del catálogo de la cápsula. */
export function estadoEstaAbierto(ticketStatuses, statusId) {
  const found = (ticketStatuses || []).find((s) => s.id === statusId);
  // Ante la duda, abierto: sub-cerrar se ve —aparece un caso de más—, sobre-cerrar no.
  if (!found) return true;
  return found.cuenta_como_abierto !== false;
}

/** Si al entrar a este estado se detiene el reloj de resolución. */
export function estadoDetieneElReloj(ticketStatuses, statusId) {
  const found = (ticketStatuses || []).find((s) => s.id === statusId);
  return Boolean(found?.detiene_el_reloj);
}
